import React from 'react';
import { SEO } from '../components/seo/SEO';
import { useParams, Link } from 'react-router-dom';
import { client, urlFor } from '../lib/sanity';
import { Calendar, User, ChevronRight, ArrowLeft } from 'lucide-react';
import { ShareButtons } from '../components/common/ShareButtons';
import type { Post } from '../types/sanity';
import { getArticleSchema, getBreadcrumbSchema } from '../lib/jsonLd';

interface PostDetails extends Post {
  body: any[];
}

interface RelatedPost {
  _id: string;
  title: string;
  slug: { current: string };
  mainImage: any;
  publishedAt: string;
  excerpt: string;
}

export function PostPage() {
  const { slug } = useParams();
  const [post, setPost] = React.useState<PostDetails | null>(null);
  const [relatedPosts, setRelatedPosts] = React.useState<RelatedPost[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const fetchPost = async () => {
      try {
        const result = await client.fetch<PostDetails | null>(`
          *[_type == "post" && slug.current == $slug && !(_id in path("drafts.**"))][0] {
            _id,
            title,
            slug,
            mainImage,
            publishedAt,
            _updatedAt,
            author->{
              name,
              image
            },
            categories[]->{
              title
            },
            body
          }
        `, { slug });

        if (!result) {
          setError('Post nie został znaleziony');
          setLoading(false);
          return;
        }

        setPost(result);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching post:', err);
        setError('Nie udało się załadować posta');
        setLoading(false);
      }
    };

    if (slug) {
      fetchPost();
    }
  }, [slug]);

  React.useEffect(() => {
    const fetchRelatedPosts = async () => {
      if (!post || !post.categories || post.categories.length === 0) return;
      
      try {
        // Get category titles
        const categoryTitles = post.categories.map(cat => cat.title);
        
        // Fetch posts with the same categories
        const result = await client.fetch<RelatedPost[]>(`
          *[_type == "post" && _id != $postId && count((categories[]->title)[@ in $categories]) > 0 && !(_id in path("drafts.**"))] | order(publishedAt desc)[0...3] {
            _id,
            title,
            slug,
            mainImage,
            publishedAt,
            excerpt
          }
        `, { 
          postId: post._id,
          categories: categoryTitles
        });
        
        setRelatedPosts(result);
      } catch (err) {
        console.error('Error fetching related posts:', err);
      }
    };

    if (post) {
      fetchRelatedPosts();
    }
  }, [post]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16">
        <p className="text-center text-xl text-accessible-gray-600 dark:text-accessible-gray-300 high-contrast:text-white">
          Ładowanie...
        </p>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16">
        <p className="text-center text-xl text-accessible-red-600 dark:text-accessible-red-500 high-contrast:text-accessible-red-500">
          {error || 'Post nie został znaleziony'}
        </p>
      </div>
    );
  }

  const getPostImage = () => {
    if (!post.mainImage) return '/images/animals/fundacja-znajdki-zwierzeta-001.webp';
    const imageUrl = urlFor(post.mainImage).width(1200).height(675).url();
    return typeof imageUrl === 'string' ? imageUrl : '/images/animals/fundacja-znajdki-zwierzeta-001.webp';
  };

  const postImage = getPostImage();
  const postUrl = `${window.location.origin}/aktualnosci/${post.slug.current}`;
  const postDescription = post.excerpt || post.title;
  
  const articleSchema = getArticleSchema(post);

  return (
    <div className="bg-accessible-gray-50 dark:bg-accessible-gray-900 high-contrast:bg-black py-16">
      <SEO 
        title={post.title}
        description={postDescription}
        url={`/aktualnosci/${post.slug.current}`}
        image={postImage}
        type="article"
        jsonLd={articleSchema}
      />
      
      {/* Breadcrumbs */}
      <div className="max-w-4xl mx-auto px-4 mb-8">
        <nav className="flex items-center text-sm text-accessible-gray-700 dark:text-white/90 high-contrast:text-white/80" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-accessible-red-600 transition-colors">
            Strona główna
          </Link>
          <ChevronRight className="w-4 h-4 mx-2" />
          <Link to="/aktualnosci" className="hover:text-accessible-red-600 transition-colors">
            Blog
          </Link>
          <ChevronRight className="w-4 h-4 mx-2" />
          <span className="text-accessible-gray-700 dark:text-white high-contrast:text-white font-medium truncate max-w-[200px]" aria-current="page">
            {post.title}
          </span>
        </nav>
      </div>
      
      <article className="max-w-4xl mx-auto px-4">
        <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black rounded-xl shadow-lg overflow-hidden mb-12">
          {/* Back button */}
          <div className="p-6 border-b border-accessible-gray-100 dark:border-accessible-gray-700 high-contrast:border-white/20">
            <Link 
              to="/aktualnosci" 
              className="inline-flex items-center gap-2 text-accessible-gray-600 dark:text-accessible-gray-300 high-contrast:text-white hover:text-accessible-red-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Powrót do bloga</span>
            </Link>
          </div>
          
          <div className="p-6">
            <div className="flex flex-wrap gap-2 mb-4">
              {post.categories?.map((category) => (
                <span
                  key={category.title}
                  className="px-3 py-1 bg-accessible-red-600 !text-white text-xs font-semibold leading-none rounded-full shadow-md"
                >
                  {category.title}
                </span>
              ))}
            </div>
            <h1 className="text-4xl font-bold text-accessible-gray-900 dark:text-white high-contrast:text-white mb-4">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 body-small text-accessible-gray-700 dark:text-white/90 high-contrast:text-white/80 no-margin mb-6">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <time dateTime={post.publishedAt}>
                  {new Date(post.publishedAt).toLocaleDateString('pl-PL', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </time>
              </div>
              {post.author && (
                <div className="flex items-center gap-2">
                  {post.author.image ? (
                    <img 
                      src={urlFor(post.author.image).width(32).height(32).url()}
                      alt={post.author.name}
                      className="w-6 h-6 rounded-full object-cover"
                    />
                  ) : (
                    <User className="w-4 h-4" />
                  )}
                  <span>{post.author.name}</span>
                </div>
              )}
            </div>

            {post.mainImage && (
              <div className="mb-8 rounded-lg overflow-hidden">
                <img
                  src={urlFor(post.mainImage)
                    .width(1200)
                    .height(675)
                    .fit('crop')
                    .crop('center')
                    .quality(90)
                    .url() || ''}
                  alt={post.title}
                  className="w-full h-auto"
                  onError={(e) => {
                    console.error('Error loading post image:', e);
                    e.currentTarget.src = 'https://placehold.co/1200x675/jpeg';
                  }}
                />
              </div>
            )}

            <div className="prose prose-lg dark:prose-invert max-w-none mb-8">
              {post.body ? (
                <div className="space-y-6">
                  {post.body.map((block, index) => (
                    <div key={index} className="mb-6">
                      {block.children?.map((child: any, childIndex: number) => {
                        // Check if this is a heading
                        if (block.style && block.style.startsWith('h')) {
                          const HeadingTag = block.style as keyof JSX.IntrinsicElements;
                          return (
                            <HeadingTag key={childIndex} className="text-accessible-gray-900 dark:text-white high-contrast:text-white font-bold mb-4">
                              {child.text}
                            </HeadingTag>
                          );
                        }
                        
                        // Regular paragraph
                        return (
                          <p key={childIndex} className="body-large text-accessible-gray-600 dark:text-accessible-gray-300 high-contrast:text-white mb-4">
                            {child.text}
                          </p>
                        );
                      })}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="body-large text-accessible-gray-600 dark:text-accessible-gray-300 high-contrast:text-white">
                  Brak treści artykułu.
                </p>
              )}
            </div>
            
            {/* Udostępnianie */}
            <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-accessible-gray-100 dark:border-accessible-gray-700 high-contrast:border-white/20">
              <ShareButtons 
                url={`${window.location.origin}/aktualnosci/${post.slug.current}`}
                title={post.title}
                description={post.excerpt}
              />
            </div>
          </div>
        </div>
        
        {/* Related Posts */}
        {relatedPosts.length > 0 && (
        <div className="mb-8">
          <h2 className="heading-3 mb-6">Powiązane artykuły</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedPosts.map((relatedPost) => (
              <Link 
                key={relatedPost._id} 
                to={`/aktualnosci/${relatedPost.slug.current}`}
                className="group"
              >
                <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black rounded-lg shadow-md overflow-hidden transition-all duration-300 transform group-hover:scale-[1.02] group-hover:shadow-lg">
                  {relatedPost.mainImage && (
                    <div className="aspect-video relative overflow-hidden">
                      <img
                        src={urlFor(relatedPost.mainImage)
                          .width(400)
                          .height(225)
                          .quality(80)
                          .url()}
                        alt={relatedPost.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="heading-5 mb-2 line-clamp-2 group-hover:text-accessible-red-600 transition-colors">
                      {relatedPost.title}
                    </h3>
                    <p className="body-small line-clamp-2">
                      {relatedPost.excerpt}
                    </p>
                    <div className="mt-2 text-xs text-accessible-gray-700 dark:text-white/90 high-contrast:text-white/70">
                      {new Date(relatedPost.publishedAt).toLocaleDateString('pl-PL')}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
        )}
      </article>
    </div>
  );
}